import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Badge,
  Tabs,
  Tab,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  DeliveryDining,
  Notifications,
  ExitToApp,
  LocationOn,
  Battery20,
  Battery50,
  Battery80,
  BatteryFull,
  Phone,
  Directions,
  CheckCircle,
  Pending,
  Security,
  Message,
  Share
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import { useNavigate } from 'react-router-dom';
import { riderService } from '../../services/riderService';
import { orderService } from '../../services/orderService';
import { chatService } from '../../services/chatService';
import VerificationWizard from '../../components/VerificationWizard';
import SimpleDocumentUpload from '../../components/SimpleDocumentUpload';
import RiderProfileEditModal from '../../components/RiderProfileEditModal';
import NotificationPanel from '../../components/NotificationPanel';
import ChatWindow from '../../components/ChatWindow';

const RiderDashboard = () => {
  const [tabValue, setTabValue] = useState(0);
  const [isAvailable, setIsAvailable] = useState(true);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationName, setLocationName] = useState(null);
  const [locationAccuracy, setLocationAccuracy] = useState(null);
  const [locationLastUpdated, setLocationLastUpdated] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [batteryLevel, setBatteryLevel] = useState(85);
  const [availableOrders, setAvailableOrders] = useState([]);
  const [myOrders, setMyOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState(null);
  const [verificationProgress, setVerificationProgress] = useState(null);
  const [documentUploadModalOpen, setDocumentUploadModalOpen] = useState(false);
  const [currentDocumentType, setCurrentDocumentType] = useState('');
  const [currentDocumentLabel, setCurrentDocumentLabel] = useState('');
  const [profileEditModalOpen, setProfileEditModalOpen] = useState(false);
  const [riderProfile, setRiderProfile] = useState(null);
  const [todayStats, setTodayStats] = useState({
    deliveries: 0,
    earnings: 0
  });
  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [currentCustomer, setCurrentCustomer] = useState(null);
  const { user, logout } = useAuth();
  const { notifications } = useSocket();
  const navigate = useNavigate();

  // Location update batching to reduce API calls
  const [pendingLocationUpdate, setPendingLocationUpdate] = useState(null);
  const locationUpdateTimeoutRef = useRef(null);
  const lastLocationUpdateRef = useRef(0);
  const lastKnownLocationRef = useRef(null);
  const LOCATION_UPDATE_DEBOUNCE = 300000; // 5 minutes minimum between location updates
  const MIN_DISTANCE_CHANGE = 100; // Minimum 100 meters change to trigger update

  // Calculate distance between two coordinates
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI/180;
    const φ2 = lat2 * Math.PI/180;
    const Δφ = (lat2-lat1) * Math.PI/180;
    const Δλ = (lon2-lon1) * Math.PI/180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c; // Distance in meters
  };

  // Get location name from coordinates using reverse geocoding with higher accuracy
  const getLocationName = useCallback(async (latitude, longitude) => {
    try {
      // Try multiple geocoding services for better accuracy
      const services = [
        // BigDataCloud - good for general areas
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`,
        // OpenStreetMap Nominatim - more detailed
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
        // Google Geocoding API (if you have API key)
        // `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=YOUR_API_KEY`
      ];

      // Try BigDataCloud first
      try {
        const response = await fetch(services[0]);
        const data = await response.json();
        
        if (data.locality && data.city) {
          return `${data.locality}, ${data.city}`;
        } else if (data.city) {
          return data.city;
        } else if (data.locality) {
          return data.locality;
        } else if (data.principalSubdivision) {
          return data.principalSubdivision;
        }
      } catch (error) {
        console.log('BigDataCloud failed, trying OpenStreetMap...');
      }

      // Try OpenStreetMap Nominatim for more detailed results
      try {
        const response = await fetch(services[1], {
          headers: {
            'User-Agent': 'PD-App/1.0'
          }
        });
        const data = await response.json();
        
        if (data.address) {
          const addr = data.address;
          // Build a more detailed address
          const parts = [];
          
          if (addr.house_number && addr.road) {
            parts.push(`${addr.house_number} ${addr.road}`);
          } else if (addr.road) {
            parts.push(addr.road);
          }
          
          if (addr.suburb || addr.neighbourhood) {
            parts.push(addr.suburb || addr.neighbourhood);
          }
          
          if (addr.city || addr.town || addr.village) {
            parts.push(addr.city || addr.town || addr.village);
          }
          
          if (addr.state) {
            parts.push(addr.state);
          }
          
          if (parts.length > 0) {
            return parts.join(', ');
          }
        }
      } catch (error) {
        console.log('OpenStreetMap failed, using coordinates...');
      }

      // Fallback to coordinates with higher precision
      return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
    } catch (error) {
      console.error('Error getting location name:', error);
      return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
    }
  }, []);

  const updateLocation = useCallback(async (location, batteryLevel, isPhoneOn = true) => {
    const now = Date.now();
    
    // Check debounce - don't update if too recent
    if (now - lastLocationUpdateRef.current < LOCATION_UPDATE_DEBOUNCE) {
      console.log('Location update skipped - too recent (debounced)');
      return;
    }
    
    // Check if location has changed significantly
    if (lastKnownLocationRef.current) {
      const distance = calculateDistance(
        lastKnownLocationRef.current.latitude,
        lastKnownLocationRef.current.longitude,
        location.latitude,
        location.longitude
      );
      
      if (distance < MIN_DISTANCE_CHANGE) {
        console.log(`Location update skipped - insufficient movement (${Math.round(distance)}m < ${MIN_DISTANCE_CHANGE}m)`);
        return;
      }
    }
    
    // Update last known location
    lastKnownLocationRef.current = { ...location };
    
    // Batch location updates - only send the latest one
    setPendingLocationUpdate({ location, batteryLevel, isPhoneOn });
    
    // Clear existing timeout
    if (locationUpdateTimeoutRef.current) {
      clearTimeout(locationUpdateTimeoutRef.current);
    }
    
    // Set new timeout to send batched update
    locationUpdateTimeoutRef.current = setTimeout(async () => {
      const update = pendingLocationUpdate;
      if (update) {
        try {
          const response = await riderService.updateLocation({
            coordinates: update.location,
            batteryLevel: update.batteryLevel,
            isPhoneOn: update.isPhoneOn
          });
          console.log('Location updated successfully:', response);
          setLocationError(null); // Clear any previous errors
          lastLocationUpdateRef.current = Date.now(); // Record successful update
        } catch (error) {
          console.error('Error updating location:', error);
          // Only set error if it's not a throttling or circuit breaker error
          if (!error.message.includes('throttled') && 
              !error.message.includes('Circuit breaker') &&
              !error.message.includes('too frequent')) {
            setLocationError(error.message);
          } else {
            // Clear error for throttling/circuit breaker cases
            setLocationError(null);
          }
        }
      }
      setPendingLocationUpdate(null);
    }, 30000); // 30 second batching window (increased from 10 seconds)
  }, [pendingLocationUpdate]);

  // Separate useEffect for initial location request (only once)
  useEffect(() => {
    let isMounted = true;
    
    const getCurrentLocation = () => {
      if (!navigator.geolocation) {
        console.error('Geolocation is not supported by this browser');
        return;
      }
      
      console.log('Requesting initial location...');
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          if (!isMounted) return;
          console.log('Initial location received:', position.coords);
          const location = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            altitude: position.coords.altitude,
            altitudeAccuracy: position.coords.altitudeAccuracy
          };
          setCurrentLocation(location);
          setLocationAccuracy(position.coords.accuracy);
          setLocationLastUpdated(new Date());
          setLocationError(null);
          
          // Get location name for display
          const locationName = await getLocationName(location.latitude, location.longitude);
          setLocationName(locationName);
          
          // Only send initial location update if rider is available
          if (isAvailable) {
            updateLocation(location, batteryLevel, true);
          }
        },
        (error) => {
          if (!isMounted) return;
          console.error('Error getting location:', error);
          
          let errorMessage = 'Location access failed';
          let errorDetails = '';
          
          switch(error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied';
              errorDetails = 'Please enable location permissions in your browser settings and refresh the page.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location unavailable';
              errorDetails = 'Unable to determine your location. Please check your GPS signal, try moving to an area with better signal, or ensure location services are enabled.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out';
              errorDetails = 'Location request took too long. Please try again.';
              break;
            default:
              errorMessage = 'Location error';
              errorDetails = 'An unknown error occurred while getting your location.';
          }
          
          setLocationError(`${errorMessage}: ${errorDetails}`);
        },
        {
          enableHighAccuracy: true,
          timeout: 30000, // Increased timeout for better accuracy
          maximumAge: 0, // Always get fresh location for initial request
          altitude: true, // Include altitude if available
          altitudeAccuracy: true
        }
      );
    };

    getCurrentLocation();
    
    return () => {
      isMounted = false;
    };
  }, []); // Empty dependency array - only run once on mount

  // Separate useEffect for battery monitoring
  useEffect(() => {
    let batteryInterval;
    
    if ('getBattery' in navigator) {
      navigator.getBattery().then((battery) => {
        const currentBatteryLevel = Math.round(battery.level * 100);
        setBatteryLevel(currentBatteryLevel);
        
        // Only update battery level every 5 minutes to reduce frequency
        batteryInterval = setInterval(() => {
          const newBatteryLevel = Math.round(battery.level * 100);
          setBatteryLevel(newBatteryLevel);
        }, 300000); // 5 minutes
      });
    }
    
    return () => {
      if (batteryInterval) {
        clearInterval(batteryInterval);
      }
    };
  }, []);

  // Separate useEffect for periodic location updates
  useEffect(() => {
    let locationInterval;
    
    // Set up periodic location updates (every 15 minutes to minimize load)
    locationInterval = setInterval(() => {
      if (navigator.geolocation && isAvailable) {
        console.log('Periodic location update...');
        navigator.geolocation.getCurrentPosition(
          (position) => {
            console.log('Periodic location received:', position.coords);
            const location = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy,
              altitude: position.coords.altitude,
              altitudeAccuracy: position.coords.altitudeAccuracy
            };
            setCurrentLocation(location);
            setLocationAccuracy(position.coords.accuracy);
            setLocationLastUpdated(new Date());
            
            // Get location name for display
            getLocationName(location.latitude, location.longitude).then(name => {
              setLocationName(name);
            });
            
            // Only send location updates to backend if rider is available
            if (isAvailable) {
              updateLocation(location, batteryLevel, true);
            }
          },
          (error) => {
            console.error('Error in periodic location update:', error);
            // Only log errors for periodic updates, don't show to user
            // This prevents spam of error messages for background location updates
          },
          {
            enableHighAccuracy: true,
            timeout: 30000, // Increased timeout for better accuracy
            maximumAge: 0, // Always get fresh location
            altitude: true, // Include altitude if available
            altitudeAccuracy: true
          }
        );
      }
    }, 900000); // 15 minutes (increased from 10 minutes)

    return () => {
      if (locationInterval) {
        clearInterval(locationInterval);
      }
      if (locationUpdateTimeoutRef.current) {
        clearTimeout(locationUpdateTimeoutRef.current);
      }
    };
  }, [isAvailable, updateLocation, batteryLevel]);

  // Separate useEffect for loadData
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setOrdersLoading(true);
    setOrdersError(null);
    try {
      const [availableOrdersData, myOrdersData, riderProfile] = await Promise.all([
        orderService.getAvailableOrders(),
        orderService.getRiderOrders(),
        riderService.getProfile()
      ]);
      setAvailableOrders(availableOrdersData || []);
      setMyOrders(myOrdersData || []);
      
      // Debug: Log order data structure
      if (myOrdersData && myOrdersData.length > 0) {
        console.log('Rider orders data structure:', myOrdersData[0]);
      }
      if (availableOrdersData && availableOrdersData.length > 0) {
        console.log('Available orders data structure:', availableOrdersData[0]);
      }
      
      // Store rider profile data
      setRiderProfile(riderProfile);
      
      // Set rider availability status from backend
      if (riderProfile) {
        setIsAvailable(riderProfile.isAvailable || true);
        if (riderProfile.currentLocation) {
          setCurrentLocation(riderProfile.currentLocation.coordinates);
          if (riderProfile.currentLocation.lastUpdated) {
            setLocationLastUpdated(new Date(riderProfile.currentLocation.lastUpdated));
          }
          
          // Get location name for display
          getLocationName(
            riderProfile.currentLocation.coordinates.latitude, 
            riderProfile.currentLocation.coordinates.longitude
          ).then(name => {
            setLocationName(name);
          });
        }
        if (riderProfile.batteryLevel !== undefined) {
          setBatteryLevel(riderProfile.batteryLevel);
        }
        if (riderProfile.verificationProgress) {
          setVerificationProgress(riderProfile.verificationProgress);
        }
      }
      
      // Calculate today's stats
      const today = new Date().toDateString();
      const todayDeliveries = (myOrdersData || []).filter(order => {
        const orderDate = new Date(order.createdAt || order.pickupTime).toDateString();
        return orderDate === today && order.status === 'delivered';
      });
      
      setTodayStats({
        deliveries: todayDeliveries.length,
        earnings: todayDeliveries.length * 250 // ₦250 per delivery
      });
    } catch (error) {
      console.error('Error loading data:', error);
      setOrdersError('Failed to load orders');
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleRetryLocation = () => {
    setLocationError(null);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const location = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            altitude: position.coords.altitude,
            altitudeAccuracy: position.coords.altitudeAccuracy
          };
          setCurrentLocation(location);
          setLocationAccuracy(position.coords.accuracy);
          setLocationLastUpdated(new Date());
          setLocationError(null);
          
          // Get location name for display
          const locationName = await getLocationName(location.latitude, location.longitude);
          setLocationName(locationName);
          
          if (isAvailable) {
            updateLocation(location, batteryLevel, true);
          }
        },
        (error) => {
          let errorMessage = 'Location access failed';
          let errorDetails = '';
          
          switch(error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied';
              errorDetails = 'Please enable location permissions in your browser settings.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location unavailable';
              errorDetails = 'Unable to determine your location. Please check your GPS signal or try moving to an area with better signal.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out';
              errorDetails = 'Location request took too long. Please try again.';
              break;
            default:
              errorMessage = 'Location error';
              errorDetails = 'An unknown error occurred while getting your location.';
          }
          
          setLocationError(`${errorMessage}: ${errorDetails}`);
        },
        {
          enableHighAccuracy: true,
          timeout: 30000, // Increased timeout for better accuracy
          maximumAge: 0, // Always get fresh location
          altitude: true, // Include altitude if available
          altitudeAccuracy: true
        }
      );
    }
  };

  const handleDocumentUpload = async (documentType, file) => {
    try {
      const response = await riderService.uploadVerificationDocument(documentType, file);
      console.log('Document upload response:', response);
      
      // Reload data to get updated verification status
      const riderProfile = await riderService.getProfile();
      if (riderProfile.verificationProgress) {
        setVerificationProgress(riderProfile.verificationProgress);
      }
      
      return response;
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  };

  const openDocumentUpload = (documentType, documentLabel) => {
    setCurrentDocumentType(documentType);
    setCurrentDocumentLabel(documentLabel);
    setDocumentUploadModalOpen(true);
  };

  const handleOpenProfileEdit = () => {
    setProfileEditModalOpen(true);
  };

  const handleCloseProfileEdit = () => {
    setProfileEditModalOpen(false);
  };

  const handleProfileUpdated = (updatedProfile) => {
    console.log('Profile updated:', updatedProfile);
    // Reload data to get updated profile information
    loadData();
  };

  const handleAcceptOrder = async (orderId) => {
    try {
      await orderService.acceptOrder(orderId);
      loadData(); // Refresh data
    } catch (error) {
      console.error('Error accepting order:', error);
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await orderService.updateOrderStatusRider(orderId, status);
      loadData(); // Refresh data
    } catch (error) {
      console.error('Error updating order status:', error);
    }
  };

  const handleContactCustomer = async (order) => {
    console.log('Contact customer button clicked for order:', order);
    console.log('Customer data:', order.customerId);
    
    const customer = order.customerId;
    if (customer && customer._id) {
      try {
        // Create or get conversation with customer (rider-customer conversation)
        const conversation = await chatService.getOrCreateRiderConversation(customer._id);
        console.log('Conversation created/retrieved:', conversation);
        
        // Set up chat
        setCurrentConversation(conversation);
        setCurrentCustomer(customer);
        setChatOpen(true);
        
        console.log('Chat window opened for customer');
      } catch (error) {
        console.error('Error opening chat with customer:', error);
        alert('Failed to open chat with customer. Please try again.');
      }
    } else {
      console.log('No customer ID available');
      alert('Customer information not available');
    }
  };

  const handleContactPharmacy = (order) => {
    console.log('Contact pharmacy button clicked for order:', order);
    console.log('Pharmacy data:', order.pharmacyId);
    
    const pharmacy = order.pharmacyId;
    if (pharmacy && pharmacy.contactInfo && pharmacy.contactInfo.phone) {
      const phoneNumber = pharmacy.contactInfo.phone;
      const cleanPhoneNumber = phoneNumber.replace(/\D/g, '');
      console.log('Pharmacy phone number:', cleanPhoneNumber);
      
      // Use phone call instead of WhatsApp
      const phoneUrl = `tel:${cleanPhoneNumber}`;
      console.log('Opening phone call URL for pharmacy:', phoneUrl);
      
      try {
        window.open(phoneUrl, '_self');
        console.log('Phone call initiated successfully for pharmacy');
      } catch (error) {
        console.error('Error initiating phone call for pharmacy:', error);
        alert('Failed to initiate phone call. Please try again.');
      }
    } else {
      console.log('No pharmacy phone number available');
      alert('Pharmacy phone number not available');
    }
  };

  const handleCloseChat = () => {
    setChatOpen(false);
    setCurrentConversation(null);
    setCurrentCustomer(null);
  };

  const handleAvailabilityChange = async (event) => {
    const available = event.target.checked;
    const previousState = isAvailable;
    
    // Optimistically update UI
    setIsAvailable(available);
    
    try {
      const response = await riderService.updateAvailability(available);
      console.log('Availability updated successfully:', response);
    } catch (error) {
      console.error('Error updating availability:', error);
      // Revert UI state on error
      setIsAvailable(previousState);
      alert('Failed to update availability. Please try again.');
    }
  };


  // const getBatteryIcon = (level) => {
  //   if (level < 20) return <Battery20 sx={{ color: 'error.main' }} />;
  //   if (level < 50) return <Battery50 sx={{ color: 'warning.main' }} />;
  //   if (level < 80) return <Battery80 sx={{ color: 'info.main' }} />;
  //   return <BatteryFull sx={{ color: 'success.main' }} />;
  // };

  const getStatusColor = (status) => {
    switch (status) {
      case 'assigned': return 'info';
      case 'picked-up': return 'warning';
      case 'delivered': return 'success';
      default: return 'default';
    }
  };

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Header */}
      <AppBar position="static" elevation={0} sx={{ bgcolor: 'white', color: 'text.primary' }}>
        <Toolbar>
          <DeliveryDining sx={{ mr: 2, color: 'secondary.main' }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: 'primary.main', fontWeight: 'bold' }}>
            Dashboard
          </Typography>
          <IconButton 
            color="inherit"
            onClick={async () => {
              try {
                const shareData = {
                  title: 'PD App - Pharmacy Delivery',
                  text: 'Check out PD App - Your trusted pharmacy delivery service!',
                  url: window.location.origin
                };
                
                if (navigator.share) {
                  await navigator.share(shareData);
                } else {
                  // Fallback for browsers that don't support Web Share API
                  await navigator.clipboard.writeText(window.location.origin);
                  alert('App link copied to clipboard!');
                }
              } catch (error) {
                console.error('Error sharing:', error);
              }
            }}
          >
            <Share />
          </IconButton>
          <IconButton 
            color="inherit"
            onClick={() => setNotificationPanelOpen(true)}
          >
            <Badge badgeContent={notifications.length} color="error">
              <Notifications />
            </Badge>
          </IconButton>
          <IconButton color="inherit" onClick={handleLogout}>
            <ExitToApp />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: { xs: 1, sm: 3 }, px: { xs: 1, sm: 3 } }}>
        {/* Welcome Section */}
        <Box sx={{ mb: { xs: 3, sm: 4 } }}>
          <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            Welcome back, {riderProfile?.firstName || 'Rider'}!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            Manage your delivery operations and earnings
          </Typography>
        </Box>


        {/* Status Cards */}
        <Grid container spacing={{ xs: 0.5, sm: 3 }} sx={{ mb: { xs: 3, sm: 4 } }}>
          <Grid size={{ xs: 4, sm: 6, md: 3 }}>
            <Card sx={{ height: { xs: 'auto', sm: 'auto' } }}>
              <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  flexDirection: { xs: 'column', sm: 'row' },
                  textAlign: { xs: 'center', sm: 'left' }
                }}>
                  <DeliveryDining sx={{ 
                    fontSize: { xs: 24, sm: 40 }, 
                    color: 'secondary.main', 
                    mr: { xs: 0, sm: 2 },
                    mb: { xs: 1, sm: 0 }
                  }} />
                  <Box>
                    <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                      {todayStats.deliveries}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        Today's Deliveries
                      </Box>
                      <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>
                        Deliveries
                      </Box>
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid size={{ xs: 4, sm: 6, md: 3 }}>
            <Card sx={{ height: { xs: 'auto', sm: 'auto' } }}>
              <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  flexDirection: { xs: 'column', sm: 'row' },
                  textAlign: { xs: 'center', sm: 'left' }
                }}>
                  <CheckCircle sx={{ 
                    fontSize: { xs: 24, sm: 40 }, 
                    color: 'success.main', 
                    mr: { xs: 0, sm: 2 },
                    mb: { xs: 1, sm: 0 }
                  }} />
                  <Box>
                    <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                      ₦{todayStats.earnings.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        Today's Earnings
                      </Box>
                      <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>
                        Earnings
                      </Box>
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Battery Level Card - Commented Out */}
          {/* <Grid size={{ xs: 4, sm: 6, md: 3 }}>
            <Card sx={{ height: { xs: 'auto', sm: 'auto' } }}>
              <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  flexDirection: { xs: 'column', sm: 'row' },
                  textAlign: { xs: 'center', sm: 'left' }
                }}>
                  <Box sx={{ 
                    mr: { xs: 0, sm: 2 },
                    mb: { xs: 1, sm: 0 }
                  }}>
                    {getBatteryIcon(batteryLevel)}
                  </Box>
                  <Box>
                    <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                      {batteryLevel}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        Battery Level
                      </Box>
                      <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>
                        Battery
                      </Box>
                    </Typography>
                  </Box>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={batteryLevel}
                  sx={{ mt: { xs: 1, sm: 1 } }}
                  color={batteryLevel < 20 ? 'error' : batteryLevel < 50 ? 'warning' : 'success'}
                />
              </CardContent>
            </Card>
          </Grid> */}
          
          <Grid size={{ xs: 4, sm: 6, md: 3 }}>
            <Card sx={{ height: { xs: 'auto', sm: 'auto' } }}>
              <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  flexDirection: { xs: 'column', sm: 'row' },
                  textAlign: { xs: 'center', sm: 'left' }
                }}>
                  <LocationOn sx={{ 
                    fontSize: { xs: 24, sm: 40 }, 
                    color: isAvailable ? 'success.main' : 'error.main', 
                    mr: { xs: 0, sm: 2 },
                    mb: { xs: 1, sm: 0 }
                  }} />
                  <Box>
                    <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                      {isAvailable ? 'Online' : 'Offline'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        {isAvailable ? 'Location Status (Visible to Pharmacies)' : 'Location Status (Hidden from Pharmacies)'}
                      </Box>
                      <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>
                        {isAvailable ? 'Available' : 'Unavailable'}
                      </Box>
                    </Typography>
                    {currentLocation && isAvailable && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                        {locationName || `${currentLocation.latitude.toFixed(6)}, ${currentLocation.longitude.toFixed(6)}`}
                        {locationAccuracy && (
                          <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                            Accuracy: ±{Math.round(locationAccuracy)}m
                          </Box>
                        )}
                      </Typography>
                    )}
                    {!isAvailable && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                        Not available for deliveries
                      </Typography>
                    )}
                    {locationLastUpdated && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                        Updated: {locationLastUpdated.toLocaleTimeString()}
                      </Typography>
                    )}
                    {locationError && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="error.main" display="block" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, mb: 1 }}>
                          Error: {locationError}
                        </Typography>
                        <Button 
                          size="small" 
                          variant="outlined" 
                          color="error"
                          onClick={handleRetryLocation}
                          sx={{ 
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                            py: 0.5,
                            px: 1,
                            minWidth: 'auto'
                          }}
                        >
                          Retry Location
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Availability Toggle */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Availability Status
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isAvailable ? 'You are available for new deliveries and visible to pharmacies' : 'You are offline and not visible to pharmacies'}
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={isAvailable}
                    onChange={handleAvailabilityChange}
                    color="success"
                  />
                }
                label={isAvailable ? 'Available' : 'Unavailable'}
              />
            </Box>
          </CardContent>
        </Card>

        {/* Main Content Tabs */}
        <Card>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                '& .MuiTab-root': {
                  fontSize: { xs: '0.7rem', sm: '0.875rem' },
                  minWidth: { xs: 'auto', sm: 'auto' },
                  px: { xs: 0.5, sm: 2 },
                  minHeight: { xs: 40, sm: 48 }
                }
              }}
            >
              <Tab 
                label={
                  <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
                      Available Orders
                    </Box>
                    <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>
                      Available
                    </Box>
                    <Badge 
                      badgeContent={availableOrders.length} 
                      color="primary"
                      sx={{ 
                        position: 'absolute',
                        top: -8,
                        right: -12,
                        '& .MuiBadge-badge': { 
                          fontSize: '0.75rem',
                          minWidth: '18px',
                          height: '18px'
                        }
                      }}
                    />
                  </Box>
                } 
              />
              <Tab 
                label={
                  <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
                      My Orders
                    </Box>
                    <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>
                      My Orders
                    </Box>
                    <Badge 
                      badgeContent={myOrders.length} 
                      color="secondary"
                      sx={{ 
                        position: 'absolute',
                        top: -8,
                        right: -12,
                        '& .MuiBadge-badge': { 
                          fontSize: '0.75rem',
                          minWidth: '18px',
                          height: '18px'
                        }
                      }}
                    />
                  </Box>
                } 
              />
              <Tab 
                label={
                  <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
                      Earnings & Statistics
                    </Box>
                    <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>
                      Earnings
                    </Box>
                    <Badge 
                      badgeContent={myOrders.filter(order => order.status === 'delivered').length} 
                      color="success"
                      sx={{ 
                        position: 'absolute',
                        top: -8,
                        right: -12,
                        '& .MuiBadge-badge': { 
                          fontSize: '0.75rem',
                          minWidth: '18px',
                          height: '18px'
                        }
                      }}
                    />
                  </Box>
                } 
              />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
                      Profile & Settings
                    </Box>
                    <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>
                      Profile
                    </Box>
                  </Box>
                } 
              />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                    <Security sx={{ fontSize: { xs: 14, sm: 16 } }} />
                    <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
                      Verification
                    </Box>
                    <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>
                      Verify
                    </Box>
                    {verificationProgress && (
                      <Chip
                        label={`${verificationProgress.progress}%`}
                        size="small"
                        color={verificationProgress.progress === 100 ? 'success' : 'primary'}
                        sx={{ 
                          fontSize: { xs: '0.7rem', sm: '0.75rem' }, 
                          height: { xs: 18, sm: 20 },
                          display: { xs: 'none', sm: 'flex' }
                        }}
                      />
                    )}
                  </Box>
                } 
              />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Available Orders in Your Area
            </Typography>
            
            {ordersLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : ordersError ? (
              <Alert severity="error" sx={{ mb: 3 }}>
                {ordersError}
              </Alert>
            ) : availableOrders.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No available orders
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Check back later for new delivery opportunities
                </Typography>
              </Box>
            ) : (
              <List>
                {availableOrders.map((order, index) => (
                <React.Fragment key={order._id || order.id}>
                  <ListItem sx={{ 
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'flex-start' },
                    gap: { xs: 2, sm: 0 }
                  }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2,
                      width: { xs: '100%', sm: 'auto' }
                    }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          <DeliveryDining />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={`${order.pharmacyId?.pharmacyName || 'Unknown Pharmacy'} → ${order.customerId?.firstName || 'Unknown'} ${order.customerId?.lastName || ''}`}
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              📍 {order.deliveryAddress?.street || 'Address not available'} • 📏 {order.distance || 'N/A'} away
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              📦 {order.items?.length || 0} items • 💰 ₦{order.totalAmount?.toLocaleString() || '0'} • 📅 {new Date(order.createdAt || order.time).toLocaleDateString()}
                            </Typography>
                          </Box>
                        }
                      />
                    </Box>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: 1,
                      width: { xs: '100%', sm: 'auto' },
                      alignItems: { xs: 'stretch', sm: 'flex-end' }
                    }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleAcceptOrder(order._id || order.id)}
                        startIcon={<CheckCircle />}
                        sx={{ 
                          minWidth: { xs: '100%', sm: 'auto' }
                        }}
                      >
                        Accept
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Directions />}
                        sx={{ 
                          minWidth: { xs: '100%', sm: 'auto' }
                        }}
                      >
                        Directions
                      </Button>
                    </Box>
                  </ListItem>
                  {index < availableOrders.length - 1 && <Divider />}
                </React.Fragment>
              ))}
              </List>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              My Active Orders
            </Typography>
            
            {ordersLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : ordersError ? (
              <Alert severity="error" sx={{ mb: 3 }}>
                {ordersError}
              </Alert>
            ) : myOrders.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No active orders
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Accept orders from the Available Orders tab to get started
                </Typography>
              </Box>
            ) : (
              <List>
                {myOrders.map((order, index) => (
                <React.Fragment key={order._id || order.id}>
                  <ListItem sx={{ 
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'flex-start' },
                    gap: { xs: 2, sm: 0 }
                  }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2,
                      width: { xs: '100%', sm: 'auto' }
                    }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'secondary.main' }}>
                          <Pending />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={`${order.pharmacyId?.pharmacyName || 'Unknown Pharmacy'} → ${order.customerId?.firstName || 'Unknown'} ${order.customerId?.lastName || ''}`}
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              📍 {order.deliveryAddress?.street || 'Address not available'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              📦 {order.items?.length || 0} items • 💰 ₦{order.totalAmount?.toLocaleString() || '0'} • 📅 {new Date(order.createdAt || order.pickupTime).toLocaleDateString()}
                            </Typography>
                          </Box>
                        }
                      />
                    </Box>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: 1, 
                      alignItems: { xs: 'stretch', sm: 'flex-end' },
                      minWidth: { xs: '100%', sm: 'auto' }
                    }}>
                      <Chip
                        label={order.status?.replace('-', ' ').toUpperCase() || 'Unknown'}
                        color={getStatusColor(order.status)}
                        size="small"
                        sx={{ 
                          textTransform: 'capitalize',
                          alignSelf: { xs: 'flex-start', sm: 'auto' }
                        }}
                      />
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 1,
                        flexWrap: 'wrap'
                      }}>
                        {order.status === 'assigned' && (
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            onClick={() => handleUpdateOrderStatus(order._id, 'picked-up')}
                            sx={{ 
                              minWidth: { xs: '100%', sm: 'auto' },
                              mb: { xs: 1, sm: 0 }
                            }}
                          >
                            Picked Up
                          </Button>
                        )}
                        {order.status === 'picked-up' && (
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            onClick={() => handleUpdateOrderStatus(order._id, 'delivered')}
                            sx={{ 
                              minWidth: { xs: '100%', sm: 'auto' },
                              mb: { xs: 1, sm: 0 }
                            }}
                          >
                            Delivered
                          </Button>
                        )}
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Message />}
                          onClick={() => {
                            console.log('Contact customer button clicked for order:', order);
                            handleContactCustomer(order);
                          }}
                          sx={{ 
                            minWidth: { xs: '100%', sm: 'auto' },
                            mb: { xs: 1, sm: 0 }
                          }}
                        >
                          Chat Customer
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Phone />}
                          onClick={() => {
                            console.log('Contact pharmacy button clicked for order:', order);
                            handleContactPharmacy(order);
                          }}
                          sx={{ 
                            minWidth: { xs: '100%', sm: 'auto' },
                            mb: { xs: 1, sm: 0 }
                          }}
                        >
                          Call Pharmacy
                        </Button>
                      </Box>
                    </Box>
                  </ListItem>
                  {index < myOrders.length - 1 && <Divider />}
                </React.Fragment>
              ))}
              </List>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Earnings & Statistics
            </Typography>
            
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      This Week
                    </Typography>
                    <Typography variant="h4" color="success.main" gutterBottom>
                      ₦{(myOrders.filter(order => order.status === 'delivered').length * 250).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {myOrders.filter(order => order.status === 'delivered').length} deliveries completed
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      This Month
                    </Typography>
                    <Typography variant="h4" color="primary.main" gutterBottom>
                      ₦{(myOrders.filter(order => order.status === 'delivered').length * 250).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {myOrders.filter(order => order.status === 'delivered').length} deliveries completed
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid size={{ xs: 12 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Recent Earnings
                    </Typography>
                    {myOrders.filter(order => order.status === 'delivered').length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          No completed deliveries yet
                        </Typography>
                      </Box>
                    ) : (
                      <List>
                        {myOrders
                          .filter(order => order.status === 'delivered')
                          .slice(0, 3)
                          .map((order, index) => (
                            <React.Fragment key={order._id || order.id}>
                              <ListItem>
                                <ListItemText
                                  primary={`Delivery #${order.orderNumber || order._id?.slice(-8)}`}
                                  secondary={`${order.pharmacyId?.pharmacyName || 'Unknown Pharmacy'} → ${order.customerId?.firstName || 'Unknown'} ${order.customerId?.lastName || ''}`}
                                />
                                <Typography variant="h6" color="success.main">
                                  ₦250
                                </Typography>
                              </ListItem>
                              {index < myOrders.filter(order => order.status === 'delivered').slice(0, 3).length - 1 && <Divider />}
                            </React.Fragment>
                          ))}
                      </List>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Profile & Settings
            </Typography>
            
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Personal Information
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Name
                      </Typography>
                      <Typography variant="body1">
                        {user?.firstName} {user?.lastName}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Phone
                      </Typography>
                      <Typography variant="body1">
                        {user?.profile?.phone}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Vehicle
                      </Typography>
                      <Typography variant="body1">
                        {user?.profile?.vehicleInfo?.type} - {user?.profile?.vehicleInfo?.plateNumber}
                      </Typography>
                    </Box>
                    <Button 
                      variant="outlined" 
                      size="small"
                      onClick={handleOpenProfileEdit}
                    >
                      Edit Profile
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Service Area
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Current Location
                      </Typography>
                      <Typography variant="body1">
                        {currentLocation 
                          ? (locationName || `${currentLocation.latitude.toFixed(6)}, ${currentLocation.longitude.toFixed(6)}`)
                          : 'Location not available'
                        }
                        {currentLocation && locationAccuracy && (
                          <Box component="div" sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              Accuracy: ±{Math.round(locationAccuracy)}m
                              {currentLocation.altitude && (
                                <span> • Altitude: {Math.round(currentLocation.altitude)}m</span>
                              )}
                            </Typography>
                          </Box>
                        )}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Service Radius
                      </Typography>
                      <Typography variant="body1">
                        10 km
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button 
                        variant="outlined" 
                        size="small"
                        onClick={() => {
                          if (navigator.geolocation) {
                            navigator.geolocation.getCurrentPosition(
                              (position) => {
                                const location = {
                                  latitude: position.coords.latitude,
                                  longitude: position.coords.longitude
                                };
                                setCurrentLocation(location);
                                setLocationLastUpdated(new Date());
                                setLocationError(null);
                                // Only send to backend if available
                                if (isAvailable) {
                                  updateLocation(location, batteryLevel, true);
                                }
                              },
                              (error) => {
                                console.error('Error getting location:', error);
                                setLocationError(error.message);
                                alert('Failed to get location. Please check your location permissions.');
                              },
                              {
                                enableHighAccuracy: true,
                                timeout: 10000,
                                maximumAge: 0
                              }
                            );
                          } else {
                            alert('Geolocation is not supported by this browser.');
                          }
                        }}
                      >
                        Update Location
                      </Button>
                      <Button 
                        variant="outlined" 
                        size="small"
                        color="secondary"
                        onClick={() => {
                          // Test location for debugging
                          const testLocation = {
                            latitude: 6.5244,
                            longitude: 3.3792
                          };
                          setCurrentLocation(testLocation);
                          setLocationLastUpdated(new Date());
                          setLocationError(null);
                          // Only send to backend if available
                          if (isAvailable) {
                            updateLocation(testLocation, batteryLevel, true);
                          }
                        }}
                      >
                        Test Location
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Account Verification
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Complete your account verification to start accepting delivery orders
            </Typography>
            
            <VerificationWizard
              verificationProgress={verificationProgress || {
                progress: 0,
                completedSteps: 0,
                totalSteps: 5,
                remainingSteps: [
                  'Complete your profile',
                  'Upload national ID',
                  'Upload driver license',
                  'Upload vehicle registration',
                  'Upload insurance certificate'
                ],
                status: 'pending',
                isVerified: false,
                steps: {
                  profileComplete: false,
                  idUploaded: false,
                  licenseUploaded: false,
                  vehicleRegistrationUploaded: false,
                  insuranceUploaded: false
                }
              }}
              userType="rider"
              onUploadDocument={(documentType) => {
                const documentLabels = {
                  'nationalId': 'National ID',
                  'driverLicense': 'Driver License',
                  'vehicleRegistration': 'Vehicle Registration',
                  'insurance': 'Insurance Certificate'
                };
                openDocumentUpload(documentType, documentLabels[documentType]);
              }}
              onSubmitVerification={async () => {
                console.log('Rider submit verification button clicked');
                try {
                  const result = await riderService.submitForVerification();
                  console.log('Rider verification submission result:', result);
                  
                  // Update verification progress immediately from the response
                  if (result && result.verificationProgress) {
                    console.log('Updating verification progress from submission response:', result.verificationProgress);
                    setVerificationProgress(result.verificationProgress);
                  }
                  
                  // Reload verification status
                  const riderProfile = await riderService.getProfile();
                  if (riderProfile.verificationProgress) {
                    setVerificationProgress(riderProfile.verificationProgress);
                    console.log('Rider verification progress updated from profile:', riderProfile.verificationProgress);
                  }
                } catch (error) {
                  console.error('Error submitting rider verification:', error);
                  // Show error message to user
                  setOrdersError('Failed to submit verification. Please try again.');
                }
              }}
            />
          </TabPanel>
        </Card>
      </Container>

      <SimpleDocumentUpload
        open={documentUploadModalOpen}
        onClose={() => setDocumentUploadModalOpen(false)}
        onUpload={handleDocumentUpload}
        documentType={currentDocumentType}
        documentLabel={currentDocumentLabel}
        verificationProgress={verificationProgress}
      />

      {/* Profile Edit Modal */}
      <RiderProfileEditModal
        open={profileEditModalOpen}
        onClose={handleCloseProfileEdit}
        profile={riderProfile}
        onProfileUpdated={handleProfileUpdated}
      />

      {/* Notification Panel */}
      <NotificationPanel 
        open={notificationPanelOpen}
        onClose={() => setNotificationPanelOpen(false)}
      />

      {/* Chat Window */}
      {chatOpen && currentConversation && currentCustomer && (
        <ChatWindow
          conversation={currentConversation}
          customer={currentCustomer}
          onClose={handleCloseChat}
          onOrderPlaced={() => {
            // Refresh orders when a new order is placed through chat
            loadData();
          }}
        />
      )}
    </Box>
  );
};

export default RiderDashboard;
